// File: app/src/apis/llm/validatePlan.js
//
// Deterministic, read-only validation of a FINALISED workout plan (issue #16).
//
// Runs after the existing post-processing pipeline (normalizeUnits →
// flattenLeafContainers → injectTransitions → coerceLegacyNodeTypes →
// validateAndAdjustTiming) and
// flags whatever those deterministic fix-ups could not resolve. "Blocking"
// issues drive the single optional LLM refinement retry in generateWorkoutPlan;
// "soft" issues are recorded on meta.validation for provenance/telemetry.
//
// Pure: never mutates the plan and never calls an LLM. Most of what the issue's
// "review with function tools" idea describes (times, equipment, structure) is
// deterministic, so it lives here rather than in a second model pass.

import { calculateTotalDuration } from "../../utils/time";
import { exerciseOf } from "../../config/exercises";
import { labelDurationSeconds } from "./postprocess";
import {
  getFirstWorkLeaf,
  getExerciseKey,
  isWorkLike,
  isContainerType,
  isRecoveryType,
} from "../../utils/planNodeUtils";

// validateAndAdjustTiming already scales timed nodes to within 15% of target.
// Only flag timing as blocking when the FINAL plan is still this far off — e.g.
// an all-reps session whose duration the timed-node scaler cannot touch.
const RESIDUAL_TIMING_TOLERANCE = 0.25;
// Over by more than this, but not blocking: recorded (soft) so the
// diagnostics show how often a session still runs long after trimming (#101).
const SOFT_OVERRUN_TOLERANCE = 0.08;

// A block whose LABEL states a duration ("16-Min E2MOM", "12 Min AMRAP") makes
// a promise the plan has to keep. Deliberately looser than the session-level
// tolerance: a reps-based block's cost is an estimate, and injected changeovers
// legitimately add to it, so only a contradiction rather than a drift should
// cost a second generation. The failure this exists for was 700% over.
const LABEL_DURATION_TOLERANCE = 0.5;

const VALID_UNITS = ["reps", "cal", "s", "m", "rounds", "sets", "workout"];

// Numeric per-set dimensions a Work's `setScheme` may vary (issue #29). Mirrors
// SET_SCHEME_DIMENSIONS in workoutCompiler.js / planNormaliser.js.
const SET_SCHEME_DIMENSIONS = ["value", "weight", "intensityPct"];

// Best-effort "asked for none but prescribed kit" keywords. Deliberately
// conservative and always SOFT — labels are free-form, so this only surfaces a
// hint and never forces a refinement on a false positive.
const EQUIPMENT_KEYWORDS = [
  "barbell",
  "dumbbell",
  "kettlebell",
  "machine",
  "cable",
  "band",
  "bench",
  "rack",
  "rower",
  "erg",
  "treadmill",
  "med ball",
  "medicine ball",
  "slam ball",
  "sandbag",
  "trx",
];

// Depth-first visit of every node in the tree.
const walk = (node, fn) => {
  if (!node || typeof node !== "object") return;
  fn(node);
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => walk(child, fn));
  }
};

// Cardio machines a plan can name, and how the kit list names them (#196).
// The prompts give machines as examples ("a bike, rower or ski erg"), and a
// model will happily prescribe one the person doesn't have. Any bike in the
// kit covers any bike in the plan: an assault bike and a spin bike both do
// for a VO2max interval.
const CARDIO_MACHINES = [
  { name: "rower", plan: /\brow(er|ing)\b|\brow\s?erg\b|\b\d+\s?m\s+row\b/i, kit: /\brow(er|ing)?\b|row\s?erg|concept\s?2/i },
  { name: "ski erg", plan: /\bski\s?-?erg\b/i, kit: /\bski/i },
  { name: "treadmill", plan: /\btreadmill\b/i, kit: /\btreadmill\b/i },
  { name: "bike", plan: /\bbike\b|\bbike\s?erg\b|\bwattbike\b|\bairdyne\b/i, kit: /\bbike\b|\bcycle\b|airdyne|wattbike/i },
  { name: "elliptical", plan: /\belliptical\b|cross[\s-]?trainer/i, kit: /elliptical|cross[\s-]?trainer/i },
  { name: "stair climber", plan: /\bstair\s?(climber|master|mill)\b/i, kit: /\bstair/i },
];
// A strength "row" is not the rowing machine.
const STRENGTH_ROW = /\b(bent|cable|seated|upright|renegade|inverted|dumbbell|barbell|kettlebell|pendlay|t-bar|chest[\s-]supported|single[\s-]arm|one[\s-]arm|band|ring|trx|machine|meadows|yates|landmine)\b/i;
// A bare "Row" step on a distance, calories or an effort cue is the machine.
const ergRow = (node) => {
  const label = String(node.label || "");
  if (!/\brow\b/i.test(label) || STRENGTH_ROW.test(label)) return false;
  const unit = String(node.unit || "").toLowerCase();
  if (unit === "cal" || (unit === "m" && Number(node.value) >= 100)) return true;
  return /\b(easy|steady|sprint|interval|zone|piece|warm|cool|hard|recovery)\b/i.test(label);
};

/** The machines a step needs that the kit list doesn't have. */
export const missingMachines = (node, kit) => {
  const label = String(node?.label || "");
  return CARDIO_MACHINES.filter((m) => {
    const named = m.name === "rower" ? m.plan.test(label) || ergRow(node) : m.plan.test(label);
    return named && !m.kit.test(kit);
  }).map((m) => m.name);
};

// Collect every prescribed-exercise leaf (excludes Groups, recovery nodes, and
// warm-up/cool-down sections).
const collectWorkLeaves = (plan) => {
  const leaves = [];
  walk(plan, (node) => {
    if (node.type === "Work") leaves.push(node);
  });
  return leaves;
};

// A warm-up or cool-down block, by type or by name. Used to separate the
// session's MAIN work from its bookends: a plan can look well-populated while
// every block in it is preparation.
const PHASE_BLOCK_LABEL = /warm[\s-]?up|cool[\s-]?down/i;

const isPhaseBlock = (node) => {
  const type = String(node?.type || "").toLowerCase();
  if (type === "warmup" || type === "cooldown") return true;
  if (PHASE_BLOCK_LABEL.test(String(node?.label || ""))) return true;
  const leafType = String(getFirstWorkLeaf(node)?.type || "").toLowerCase();
  return leafType === "warmup" || leafType === "cooldown";
};

// No-faff limits (#85): pieces of kit for the whole session, and distinct
// prescribed weights before it counts as fiddling with loads.
const MAX_NOFAFF_KIT = 2;
const MAX_NOFAFF_LOADS = 2;

/**
 * What a plan's main work needs set up, from the exercise library:
 *   kit     the fewest pieces of equipment that cover every exercise
 *           (greedy set cover — each library entry accepts any one of its kit)
 *   fiddly  labels of exercises with setup cost 3 (barbell, rack, landmine)
 *   loads   distinct prescribed weights
 */
export const summariseSetup = (plan) => {
  const needs = [];
  const fiddly = new Set();
  const weights = new Set();
  const visit = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach(visit);
      return;
    }
    if (node.type !== "Work") return;
    if (Number.isFinite(node.weight) && node.weight > 0) weights.add(node.weight);
    const exercise = exerciseOf(node);
    if (!exercise) return;
    if (exercise.setup >= 3) fiddly.add(node.label);
    if (exercise.equipment.length > 0) needs.push(exercise.equipment);
  };
  collectMainBlocks(plan).forEach(visit);

  const kit = [];
  let open = needs;
  while (open.length > 0) {
    const counts = new Map();
    open.forEach((options) =>
      options.forEach((k) => counts.set(k, (counts.get(k) || 0) + 1)),
    );
    const [best] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    kit.push(best);
    open = open.filter((options) => !options.includes(best));
  }
  return { kit, fiddly: [...fiddly], loads: weights.size };
};

// Root-level blocks holding the session's actual training content.
const collectMainBlocks = (plan) =>
  (Array.isArray(plan.children) ? plan.children : []).filter(
    (child) => isWorkLike(child) && !isPhaseBlock(child),
  );

// Distinct exercises prescribed by the main blocks, by normalised label.
const collectMainExerciseKeys = (plan) => {
  const keys = new Set();
  const visit = (node) => {
    if (!node || typeof node !== "object" || isRecoveryType(node.type)) return;
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach(visit);
      return;
    }
    const key = getExerciseKey(node);
    if (key) keys.add(key);
  };
  collectMainBlocks(plan).forEach(visit);
  return keys;
};

// The styles the session was asked for. Mirrors buildPrompt's resolution so a
// coverage complaint always matches what the prompt actually demanded.
const requestedStyles = (userProfile) => {
  const { workoutStyle, workoutStyles } = userProfile;
  return Array.isArray(workoutStyles) && workoutStyles.length
    ? workoutStyles
    : [workoutStyle || "mixed"].filter(Boolean);
};

/**
 * Validate a finalised workout plan against the user's request.
 *
 * @param {Object} plan        – The finalised workout_plan tree.
 * @param {Object} userProfile – The same profile passed to buildPrompt.
 * @returns {{ issues: Array<{code:string,severity:"blocking"|"soft",message:string,nodeId?:string}>, blocking: boolean }}
 */
const validatePlan = (plan, userProfile = {}) => {
  const issues = [];
  const add = (code, severity, message, nodeId) =>
    issues.push(
      nodeId ? { code, severity, message, nodeId } : { code, severity, message },
    );

  // 1. Structure — a plan with no work leaf at all is unusable.
  if (!plan || typeof plan !== "object") {
    add("structure.missing", "blocking", "Plan is missing or not an object.");
    return { issues, blocking: true };
  }
  if (!getFirstWorkLeaf(plan)) {
    add(
      "structure.no_work",
      "blocking",
      "Plan contains no performable Work/Warmup/Cooldown leaf.",
    );
  }

  // 1a. Only a container type may hold `children` (docs/schema.md).
  //     flattenLeafContainers repairs this shape before we ever get here, so a
  //     survivor means a root-level malformation the pass deliberately leaves
  //     alone. BLOCKING, because the cost is silent: the compiler descends into
  //     any node with children but emits only leaves, so every prescription
  //     above the deepest child is dropped without a trace in the UI.
  walk(plan, (node) => {
    if (
      !isContainerType(node.type) &&
      Array.isArray(node.children) &&
      node.children.length > 0
    ) {
      add(
        "structure.leafWithChildren",
        "blocking",
        `"${node.label || node.id}" is a ${node.type} but holds ${node.children.length} child node(s). Only a Group holds children — a Work, Warmup, Cooldown or Rest node is a step, never a container. Emit the steps as SIBLINGS inside a Group instead of nesting them inside each other.`,
        node.id,
      );
    }
  });

  const workLeaves = collectWorkLeaves(plan);

  // 2. Residual timing — validateAndAdjustTiming already scaled to ±15%; flag
  //    only when the plan is STILL well outside the requested duration AND has
  //    timed content (an all-reps plan reports 0s and is left alone).
  const timeAvailable = Number(userProfile.timeAvailable);
  if (Number.isFinite(timeAvailable) && timeAvailable > 0) {
    const targetSeconds = timeAvailable * 60;
    const actualSeconds = calculateTotalDuration(plan, {
      skipLastRest: userProfile.skipLastRest ?? true,
    });
    if (actualSeconds > 0) {
      const deviation = Math.abs(actualSeconds - targetSeconds) / targetSeconds;
      if (deviation > RESIDUAL_TIMING_TOLERANCE) {
        // Say what to DO, not just how far off it is. "Off by 95%" is a
        // measurement; the refinement pass needs a direction, because the two
        // directions need opposite edits and the under-filled case needs
        // content the plan does not yet contain.
        const actualMinutes = Math.round(actualSeconds / 60);
        const shortfall = Math.max(
          0,
          Math.round((targetSeconds - actualSeconds) / 60),
        );
        const remedy =
          actualSeconds < targetSeconds
            ? `Too short by ~${shortfall} min. ADD work to the main blocks — more exercises first, then more sets or rounds — until the session fills ${timeAvailable} min. Do not pad the warm-up, cool-down or rest periods to cover it.`
            : `Too long. REMOVE work from the main blocks — drop exercises or sets — until the session fits ${timeAvailable} min.`;
        add(
          "timing.residual",
          "blocking",
          `Session runs ~${actualMinutes} min vs the requested ${timeAvailable} min (off by ${Math.round(deviation * 100)}%). ${remedy}`,
        );
      } else if (actualSeconds > targetSeconds * (1 + SOFT_OVERRUN_TOLERANCE)) {
        add(
          "timing.over",
          "soft",
          `Session runs ~${Math.round(actualSeconds / 60)} min vs the requested ${timeAvailable} min.`,
        );
      }
    }
  }

  // 2a. A block must cost what its own label says. This is the check that
  //     catches a block authored inside another block's rounds: the nesting
  //     multiplies its cost, so "16-Min E2MOM" bills 128 minutes while every
  //     other pass stands aside — enforceEmomWindows bails on a Group child and
  //     the scaler exempts protocol labels. hoistMisplacedBlocks repairs the
  //     shape it can prove; this is what catches the rest, and it reports the
  //     arithmetic so the refinement pass has something to act on.
  walk(plan, (node) => {
    if (node.type !== "Group") return;
    const stated = labelDurationSeconds(node.label);
    if (stated == null || stated <= 0) return;
    const actual = calculateTotalDuration(node, {
      skipLastRest: userProfile.skipLastRest ?? true,
    });
    if (actual <= 0) return;
    if (Math.abs(actual - stated) / stated <= LABEL_DURATION_TOLERANCE) return;
    const minutes = (seconds) => Math.round(seconds / 60);
    add(
      "structure.labelDuration",
      "blocking",
      `Block "${node.label}" states ${minutes(stated)} min but its contents cost ${minutes(actual)} min. ${
        actual > stated
          ? "A block nested inside this one runs once per round — move it OUT to be a separate top-level block, or reduce this block's contents."
          : "Add work until the block fills the duration its label states, or change the label."
      }`,
      node.id,
    );
  });

  // 3. Unit sanity — normalizeUnits should have coerced these; a survivor means
  //    a malformed node. Soft (the runtime tolerates it).
  walk(plan, (node) => {
    if (
      node.unit != null &&
      !VALID_UNITS.includes(String(node.unit).toLowerCase())
    ) {
      add(
        "units.invalid",
        "soft",
        `Node "${node.label || node.id}" has unexpected unit "${node.unit}".`,
        node.id,
      );
    }
  });

  // 4. Guidance — buildPrompt asks for an intensityTarget on every Work node.
  //    Soft: a missing cue degrades coaching but is not unusable.
  workLeaves.forEach((node) => {
    const cue =
      typeof node.intensityTarget === "string"
        ? node.intensityTarget.trim()
        : "";
    if (!cue) {
      add(
        "guidance.missing",
        "soft",
        `Work node "${node.label || node.id}" has no intensityTarget cue.`,
        node.id,
      );
    }
  });

  // 5. Equipment — best-effort, always SOFT. If the user declared no equipment
  //    but an exercise label names kit, surface it as a hint only.
  const equip = String(userProfile.availableEquipment || "")
    .trim()
    .toLowerCase();
  const hasNoEquipment =
    equip === "" || equip === "none" || equip === "bodyweight";
  // A cardio machine missing from a declared kit list is BLOCKING (#196): a
  // VO2max session built around a rower you don't own can't be done as
  // written, and the refinement pass can swap in a machine you have.
  // Only with a declared list: no answer at all says nothing about the kit.
  if (equip !== "") {
    walk(plan, (node) => {
      const type = String(node.type || "").toLowerCase();
      if (type !== "work" && type !== "warmup" && type !== "cooldown") return;
      const missing = missingMachines(node, equip);
      if (missing.length) {
        add(
          "equipment.machine_unavailable",
          "blocking",
          `"${node.label}" needs a ${missing.join(" and ")}, which isn't in the user's equipment (${userProfile.availableEquipment}). Use a machine from that list instead, or running if there is none.`,
          node.id,
        );
      }
    });
  }
  if (hasNoEquipment) {
    workLeaves.forEach((node) => {
      const label = String(node.label || "").toLowerCase();
      const hit = EQUIPMENT_KEYWORDS.find((kw) => label.includes(kw));
      if (hit) {
        add(
          "equipment.unavailable",
          "soft",
          `"${node.label}" appears to need equipment ("${hit}") but the profile lists none.`,
          node.id,
        );
      }
    });
  }

  // 6. Coverage — a multi-style request names the blocks it wants, and the
  //    prompt asks for one DISTINCT block per style. A session that comes back
  //    as a single block, or with every set on the same movement, has not
  //    answered the request at all — a "hypertrophy + conditioning" ask cannot
  //    be four sets of back squat. BLOCKING, because no deterministic pass can
  //    invent the missing block; only the model can.
  //
  //    Gated on a MULTI-style request on purpose. A single-style session
  //    legitimately runs one block, and "just squats today" is a real session —
  //    flagging those would spend a second generation to reject a correct plan.
  const styles = requestedStyles(userProfile);
  if (styles.length > 1) {
    const mainBlocks = collectMainBlocks(plan);
    const exerciseKeys = collectMainExerciseKeys(plan);
    const styleList = styles.join(" + ");
    if (mainBlocks.length <= 1) {
      add(
        "coverage.blocks",
        "blocking",
        `This session was requested as ${styles.length} styles (${styleList}) but the main work is ${mainBlocks.length === 0 ? "empty" : "a single block"}. Emit one DISTINCT block per style, sequenced physiologically, each with its own exercises.`,
      );
    }
    if (exerciseKeys.size <= 1) {
      add(
        "coverage.monotony",
        "blocking",
        `The whole main workout is ${exerciseKeys.size === 0 ? "empty" : `one exercise ("${[...exerciseKeys][0]}")`}. A ${styleList} session needs different movements across its blocks — add the exercises each style calls for.`,
      );
    }
  }

  // 7. Set scheme — a Work's per-set arrays must use known dimensions, hold
  //    finite numbers, and match the enclosing sets-Group's set count. A length
  //    mismatch is BLOCKING: the compiler falls back to static values for the
  //    unmatched sets, so the prescribed ramp would silently be wrong. Set
  //    COUNT itself is always safe (governed by the Group's value), so this can
  //    never multiply volume — it just drives the single refinement retry.
  const checkSetScheme = (node, enclosingGroupValue) => {
    if (!node || typeof node !== "object") return;
    // A scheme anywhere but on a Work is not resolved by the compiler — per-set
    // values are applied to leaves only, so the block would silently run set 1
    // for every set (issue #74). The normaliser lifts the unambiguous case onto
    // the Group's single Work child; say so regardless so the model stops
    // authoring it there.
    if (node.type !== "Work" && node.setScheme != null) {
      add(
        "setScheme.wrongNode",
        "soft",
        `${node.type} "${node.label || node.id}" carries a setScheme. Per-set arrays belong on the Work node they describe, not on its Group.`,
        node.id,
      );
    }
    if (node.type === "Work" && node.setScheme != null) {
      const scheme = node.setScheme;
      const label = node.label || node.id;
      if (typeof scheme !== "object" || Array.isArray(scheme)) {
        add(
          "setScheme.malformed",
          "blocking",
          `Work "${label}" has a non-object setScheme.`,
          node.id,
        );
      } else {
        Object.keys(scheme).forEach((dim) => {
          if (!SET_SCHEME_DIMENSIONS.includes(dim)) {
            add(
              "setScheme.unknownDim",
              "soft",
              `Work "${label}" setScheme has unknown dimension "${dim}".`,
              node.id,
            );
            return;
          }
          const arr = scheme[dim];
          if (
            !Array.isArray(arr) ||
            !arr.every((v) => Number.isFinite(Number(v)))
          ) {
            add(
              "setScheme.malformed",
              "blocking",
              `Work "${label}" setScheme.${dim} must be an array of numbers.`,
              node.id,
            );
            return;
          }
          if (
            Number.isFinite(enclosingGroupValue) &&
            arr.length !== enclosingGroupValue
          ) {
            add(
              "setScheme.lengthMismatch",
              "blocking",
              `Work "${label}" setScheme.${dim} has ${arr.length} entries but its sets-Group runs ${enclosingGroupValue} set(s).`,
              node.id,
            );
          }
        });
      }
    }
    if (Array.isArray(node.children)) {
      const childGroupValue =
        node.type === "Group" && Number(node.value) > 0
          ? Number(node.value)
          : enclosingGroupValue;
      node.children.forEach((c) => checkSetScheme(c, childGroupValue));
    }
  };
  checkSetScheme(plan, null);

  // 8. No faff (#85) — a session asked to be quick to set up must not need a
  //    barbell, a rack or a kit tour. BLOCKING when it plainly breaks the
  //    request (fiddly setup, or 4+ pieces of kit); soft at the margin.
  if (userProfile.noFaff) {
    const { kit, fiddly, loads } = summariseSetup(plan);
    if (fiddly.length > 0) {
      add(
        "nofaff.setup",
        "blocking",
        `No-faff session, but ${fiddly.map((n) => `"${n}"`).join(", ")} need${fiddly.length === 1 ? "s" : ""} barbell, rack or landmine setup. Replace with low-setup movements (bodyweight, one kettlebell or dumbbell, slam ball, sandbag, an erg).`,
      );
    }
    if (kit.length > MAX_NOFAFF_KIT + 1) {
      add(
        "nofaff.kit",
        "blocking",
        `No-faff session, but it needs ${kit.length} pieces of kit (${kit.join(", ")}). Use at most ${MAX_NOFAFF_KIT}, set up once.`,
      );
    } else if (kit.length > MAX_NOFAFF_KIT) {
      add(
        "nofaff.kit",
        "soft",
        `No-faff session uses ${kit.length} pieces of kit (${kit.join(", ")}); ${MAX_NOFAFF_KIT} is the target.`,
      );
    }
    if (loads > MAX_NOFAFF_LOADS) {
      add(
        "nofaff.loads",
        "soft",
        `No-faff session prescribes ${loads} different weights; keep to one load per implement.`,
      );
    }
  }

  return { issues, blocking: issues.some((i) => i.severity === "blocking") };
};

export { validatePlan };
